import React, { useEffect, useRef, useState } from "react";
import SearchInput from "../../../constants/SearchInput";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import { useNetwork } from "../../../context/NetworkContext";
import { usePermission } from "../../../context/PermissionContext";
import Messages from "../../../constants/Messages";
import AddSupplier from "../supplier/AddSupplier";
import EditSupplier from "../supplier/EditSupplier";
import GetServerLiveTime from "../../../constants/GetServerLiveTime";
import DateConverter from "@heartinz/date_and_time_processor";
import { useAuth } from "../../../context/AuthProvider";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CalendarIcon, CheckIcon, EditIcon, XIcon } from "../../../constants/Icons";

const AddPurchase = ({ onClose, handleAdd, isActive }) => {
  const { checkNetwork } = useNetwork();
  const [error, setErrors] = useState({});
  const { isUser } = useAuth();
  const serverTime = GetServerLiveTime();
  const iso = serverTime.toISOString();
  const { permission, blockedPermissions, customerGroups } = usePermission();
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [purchaseDate, setPurchaseDate] = useState(dayjs());
  const [purchaseNumber, setPurchaseNumber] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [isCustomerLoading, setIsCustomerLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [isCustomerModalOpen, setCustomerModalOpen] = useState(false);
  const [isCustomerData, setCustomerData] = useState(null);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isEditData, setEditData] = useState(null);
  const [numberSuggest, setNumberSuggest] = useState([]);
  const [isBtnLoading, setBtnLoading] = useState(false);
  const [groupName, setGroupName] = useState(customerGroups);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const showAlert = useShowAlert();
  const customerSuggestionsRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const controllerRef = useRef(null);

  const dateRef = useRef();
  const numberRef = useRef();
  const addButtonRef = useRef();
  const suggestionRefs = useRef([]);
  const datePickerRef = useRef(null);

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && customerSearchTerm.trim()) {
      handleCustomerModalOpen();
    }
  };

  const handleCustomerModalOpen = () => {
    setSelectedCustomer(null);
    setCustomerModalOpen(true);
  };

  const handleCustomerModalClose = () => {
    setCustomerModalOpen(false);
    setCustomerData(null);
  };

  const handleResetCustomer = () => {
    setCustomerModalOpen(false);
    setCustomerData(null);
  };

  const closeEditModal = () => {
    setEditData(null);
    setEditModalOpen(false);
  };

  const handleCashDate = (selectedDate) => {
    if (!selectedDate) return;

    const isoDateTime = selectedDate.toISOString();
    setPurchaseDate(selectedDate);
  };

  const addNewCustomNumber = (phone) => {
    const trimmed = phone?.trim();

    if (!trimmed) return; // ignore empty/whitespace input

    const exists = numberSuggest?.some(
      (s) => s.phone?.toLowerCase() === trimmed?.toLowerCase(),
    );

    if (!exists) {
      setNumberSuggest((prev) => [
        { _id: Date.now().toString(), phone: trimmed },
        ...prev,
      ]);
    }
  };

  const handleSupplierSearch = (term) => {
    setCustomerSearchTerm(term);
    if (term.trim() === "") {
      setCustomerSuggestions([]);
      setCustomerData(null);
      return;
    }
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    debounceTimerRef.current = setTimeout(async () => {
      try {
        setIsCustomerLoading(true);
        if (!checkNetwork()) return;
        controllerRef.current = new AbortController();
        const response = await AxiosInstance.get(
          `module/supplier/search?searchtxt=${encodeURIComponent(term.trim())}`,
          { signal: controllerRef.current.signal },
        );
        setCustomerSuggestions(response.data.results || []);
        const data = { phone: "", name: "" };
        if (/^\d+$/.test(term.trim())) {
          data.phone = term.trim();
        } else {
          data.name = term.trim();
        }
        setCustomerData(data);
      } catch (error) {
        if (error.name !== "CanceledError" && error.name !== "AbortError") {
          handleError(error, showAlert);
        }
      } finally {
        setIsCustomerLoading(false);
      }
    }, 200);
  };

  const handleSelectCustomer = async (customer) => {
    try {
      if (!checkNetwork()) return;

      // 1. Get full supplier details
      const response = await AxiosInstance.get(
        `module/supplier/${customer._id}`,
      );

      const fullCustomer = response.data.results;

      // 2. Update UI state
      setSelectedCustomer(fullCustomer);
      setCustomerData(fullCustomer);
      setCustomerSearchTerm("");
      setCustomerSuggestions([]);

      // ❌ REMOVE THIS BLOCK COMPLETELY
      // updateActiveCart((cart) => ({
      //   ...cart,
      //   customer: fullCustomer,
      // }));
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const handleCustomerAdd = async (newData) => {
    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      const data = {
        ...newData,
        ...(newData.contactPersonname?.trim() ||
        newData.contactPersonNumber?.trim()
          ? {
              contactPerson: {
                ...(newData.contactPersonname?.trim() && {
                  name: newData.contactPersonname,
                }),
                ...(newData.contactPersonNumber?.trim() && {
                  number: newData.contactPersonNumber,
                }),
              },
            }
          : {}),
      };
      const response = await AxiosInstance.post(`module/supplier`, data);
      showAlert("success", Messages.SUCCESS.CUSTOMER_ADDED);
      handleResetCustomer();
      handleSelectCustomer(response.data.results);
      return true;
    } catch (error) {
      if (
        error.response &&
        error.response.status === 409 &&
        error.response.data &&
        error.response.data.code === "DUPLICATE_ENTRY"
      ) {
        const errorMessage = error.response.data.message;
        let fieldError = {};

        // if (errorMessage.includes("ReferenceId already exists")) {
        //     fieldError.referenceid = "Reference ID already exists .";
        // }
        if (errorMessage.includes("GST number already exists")) {
          fieldError.gstnumber = "GST number already exists.";
        }

        if (Object.keys(fieldError).length > 0) {
          setErrors((prev) => ({ ...prev, ...fieldError }));
        } else {
          handleError(error, showAlert);
        }

        return false;
      } else {
        handleError(error, showAlert);
        return false;
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const handleUpdte = async (updatedData) => {
    const editableKeys = [
      "name",
      "number",
      "email",
      "addressline1",
      "addressline2",
      "city",
      "district",
      "state",
      "pincode",
      "country",
      "gstnumber",
      "pannumber",
      "cinnumber",
      "referenceid",
      "isPayLater",
      "groupid",
      "website",
      "fssai",
      "tradeName",
      "nickname",
      "contactPersonname",
      "contactPersonNumber",
    ];

    let hasChange = false;
    for (const key of editableKeys) {
      const originalValue = String(isEditData[key] || "");
      const updatedValue = String(updatedData[key] || "");
      if (originalValue !== updatedValue) {
        hasChange = true;
        break;
      }
    }
    if (!hasChange) {
      showAlert("warning", "No changes detected.");
      return false;
    }
    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      const data = {
        ...updatedData,
        ...(updatedData.contactPersonname?.trim() ||
        updatedData.contactPersonNumber?.trim()
          ? {
              contactPerson: {
                ...(updatedData.contactPersonname?.trim() && {
                  name: updatedData.contactPersonname,
                }),
                ...(updatedData.contactPersonNumber?.trim() && {
                  number: updatedData.contactPersonNumber,
                }),
              },
            }
          : {}),
        customerid: updatedData._id,
      };
      const response = await AxiosInstance.patch(
        `module/supplier/${updatedData._id}`,
        data,
      );

      showAlert("success", Messages.SUCCESS.CUSTOMER_UPDATED);
      setCustomerData(response.data.results);
      setSelectedCustomer((prev) => ({
        ...prev,
        customerData: response.data.results,
      }));
      closeEditModal();
      return true;
    } catch (error) {
      if (
        error.response &&
        error.response.status === 409 &&
        error.response.data &&
        error.response.data.code === "DUPLICATE_ENTRY"
      ) {
        const errorMessage = error.response.data.message;
        let fieldError = {};

        if (errorMessage.includes("ReferenceId already exists")) {
          fieldError.referenceid = "Reference ID already exists .";
        } else if (errorMessage.includes("GST number already exists")) {
          fieldError.gstnumber = "GST number already exists.";
        }

        if (Object.keys(fieldError).length > 0) {
          setErrors((prev) => ({ ...prev, ...fieldError }));
        } else {
          handleError(error, showAlert);
        }

        return false;
      } else {
        handleError(error, showAlert);
        return false;
      }
    } finally {
      setBtnLoading(false);
    }
  };

  // const handleCustomerKeyDown = (e) => {
  //   if (e.key === "ArrowDown") {
  //     e.preventDefault();
  //     setHighlightedIndex((prev) =>
  //       Math.min(prev + 1, customerSuggestions.length - 1),
  //     );
  //   } else if (e.key === "ArrowUp") {
  //     e.preventDefault();
  //     setHighlightedIndex((prev) => Math.max(prev - 1, 0));
  //   } else if (e.key === "Enter") {
  //     e.preventDefault();

  //     const trimmedSearch = customerSearchTerm.trim();

  //     const noSelection = highlightedIndex === -1;
  //     const hasExactMatch = customerSuggestions.some((customer) => {
  //       const nameMatch =
  //         customer.name.toLowerCase() === trimmedSearch.toLowerCase();
  //       const phoneMatch = customer.phone === trimmedSearch;
  //       return nameMatch || phoneMatch;
  //     });

  //     if (noSelection && !hasExactMatch) {
  //       handleCustomerModalOpen();
  //       return;
  //     }

  //     if (highlightedIndex >= 0) {
  //       const selected = customerSuggestions[highlightedIndex];
  //       handleSelectCustomer(selected);
  //       customerSuggestionsRef.current?.focus();
  //       setShowCustomerDropdown(false);
  //       setCustomerSearchTerm("");
  //       setHighlightedIndex(-1);
  //     }
  //   }
  // };

  const handleCustomerKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        Math.min(prev + 1, customerSuggestions.length - 1),
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();

      const trimmedSearch = customerSearchTerm.trim();

      // ✅ CASE 1: If supplier already selected → trigger ADD
      if (selectedCustomer?._id && !trimmedSearch) {
        addButtonRef.current?.click();
        return;
      }

      const noSelection = highlightedIndex === -1;

      const hasExactMatch = customerSuggestions.some((customer) => {
        const nameMatch =
          customer.name.toLowerCase() === trimmedSearch.toLowerCase();
        const phoneMatch = customer.phone === trimmedSearch;
        return nameMatch || phoneMatch;
      });

      // ✅ CASE 2: No suggestion → open AddSupplier
      if (noSelection && !hasExactMatch) {
        handleCustomerModalOpen();
        return;
      }

      // ✅ CASE 3: Select from suggestions
      if (highlightedIndex >= 0) {
        const selected = customerSuggestions[highlightedIndex];
        handleSelectCustomer(selected);
        customerSuggestionsRef.current?.focus();
        setShowCustomerDropdown(false);
        setCustomerSearchTerm("");
        setHighlightedIndex(-1);
      }
    }
  };

  const openEditModal = (cItem) => {
    if (
      blockedPermissions("CUSTOMER:viewDetails") ||
      permission === undefined
    ) {
      setEditData(cItem);
      setEditModalOpen(true);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && suggestionRefs.current[highlightedIndex]) {
      suggestionRefs.current[highlightedIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  // useEffect(() => {
  //   const handleEsc = (e) => {
  //     if (e.key === "Escape") {
  //       onClose();
  //     }
  //   };

  //   window.addEventListener("keydown", handleEsc);
  //   return () => window.removeEventListener("keydown", handleEsc);
  // }, [onClose]);


useEffect(() => {
  const today = DateConverter.convertToTimeZone(
    serverTime.toISOString(),
    isUser?.timezone
  );

  setPurchaseDate(dayjs(today));
}, [isUser?.timezone]);

  return (
    <>
      <div className="modal-backdrop show"></div>
      <div className="modal show" tabIndex="-1" style={{ display: "block" }}>
        <div className="modal-dialog modal-md modal-dialog-centered">
          <div className="modal-content">
            {/* HEADER */}
            <div className="modal-header">
              <h5 className="modal-title">Create Purchase</h5>
              <button className="btn-close" onClick={onClose}></button>
            </div>

            {/* BODY */}
            <div className="modal-body  bg-light">
              <div className="card">
                <div className="card-body">
                  {/* DATE */}
                  <div className="col-lg-6">
                    <label className="form-label">
                      Purchase Date <span className="text-danger">*</span>
                    </label>

                    <div className="input-group" ref={datePickerRef}>
                      <button type="button" className="input-group-text">
                        <CalendarIcon/>
                      </button>

                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          open={openDatePicker}
                          onOpen={() => setOpenDatePicker(true)}
                          onClose={() => setOpenDatePicker(false)}
                          value={purchaseDate}
                          ref={dateRef}
                          maxDate={dayjs(serverTime)}
                          format="DD MMM YYYY"
                          disableOpenPicker
                          onChange={(newValue) => {
                            handleCashDate(newValue);
                            setOpenDatePicker(false);
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              className: "form-control cursor-pointer",
                              inputProps: { readOnly: true },
                              onClick: () => setOpenDatePicker(true),
                              onKeyDown: (e) => e.preventDefault(),
                              sx: {
                                "& .MuiInputBase-input": {
                                  cursor: "pointer",
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </div>

                    {/* <input
                      type="date"
                      className="form-control"
                      ref={dateRef}
                      value={purchaseDate}
                      autoFocus
                      onChange={(e) => setPurchaseDate(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          numberRef.current?.focus();
                        }
                      }}
                    /> */}
                  </div>
                  {/* PURCHASE NUMBER */}
                  <div className="col-lg-6 mt-3">
                    <label className="form-label">
                      Purchase Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      ref={numberRef}
                      placeholder="Enter purchase number"
                      value={purchaseNumber}
                      onChange={(e) => setPurchaseNumber(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          customerSuggestionsRef.current.focus();
                        }
                      }}
                    />
                  </div>

                  <div className="mt-2">
                    <label className="form-label">
                      Supplier <span className="text-danger">*</span>
                    </label>
                    <div className="card">
                      <div className="card-body ">
                        <div className="ms-auto position-relative">
                          <SearchInput
                            searchRef={customerSuggestionsRef}
                            value={customerSearchTerm}
                            onChange={(e) => {
                              handleSupplierSearch(e.target.value);
                              setShowCustomerDropdown(true);
                              setHighlightedIndex(-1);
                            }}
                            onFocus={() => {
                              if (customerSuggestions.length > 0) {
                                setShowCustomerDropdown(true);
                              }
                            }}
                            onSearch={
                              customerSearchTerm.trim()
                                ? () =>
                                    handleCustomerModalOpen(
                                      customerSearchTerm.trim(),
                                    )
                                : null
                            }
                            onKeyPress={handleKeyPress}
                            placeholder="Search customer by name, phone, email, referenceID..."
                            onKeyDown={handleCustomerKeyDown}
                          />
                          {customerSearchTerm &&
                            customerSuggestions.length === 0 && (
                              <ul className="suggestions-list">
                                <li className="suggestion-item text-muted">
                                  No customers data was found
                                </li>
                              </ul>
                            )}
                          {showCustomerDropdown &&
                            customerSuggestions.length > 0 && (
                              <ul className="suggestions-list">
                                {customerSuggestions.map((customer, index) => (
                                  <li
                                    ref={(el) =>
                                      (suggestionRefs.current[index] = el)
                                    }
                                    key={customer._id}
                                    onClick={() =>
                                      handleSelectCustomer(customer)
                                    }
                                    className={`suggestion-item ${
                                      highlightedIndex === index
                                        ? "highlighted"
                                        : ""
                                    }`}
                                  >
                                    {customer.name} -{" "}
                                    <span className="badge badge-primary badge bg-label-blue rounded-pill">
                                      {customer.number}
                                    </span>
                                    {customer.referenceid && (
                                      <>
                                        <small>-</small>{" "}
                                        <span className="badge badge-info badge bg-label-secondary rounded-pill">
                                          {customer.referenceid}
                                        </span>
                                      </>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                        </div>
                        <div className="col-lg-12 mt-1">
                          <div className="card shadow-none border-blue mb-0">
                            <div className="card-header bg-light-blue p-2 d-flex justify-content-between align-items-center">
                              <div className="mb-0">Supllier Info</div>
                              {selectedCustomer?._id && (
                                <div>
                                  <button
                                    type="button"
                                    className="btn-label-primary rounded  shadow-none border-0"
                                    onClick={() =>
                                      openEditModal(selectedCustomer)
                                    }
                                  >
                                    <EditIcon/>
                                  </button>
                                </div>
                              )}
                            </div>
                            <div className="card-body px-2 py-0">
                              {selectedCustomer ? (
                                <>
                                  <p className="my-1 fs-13">
                                    Name :{" "}
                                    <span className="fw-600 fs-14">
                                      {selectedCustomer.name}
                                    </span>
                                  </p>
                                  <p className="my-1 fs-13">
                                    Number :{" "}
                                    <span className="fw-600 fs-14">
                                      {selectedCustomer.number}{" "}
                                    </span>
                                  </p>
                                  {/* {selectedCustomer.customerData.gstnumber && (
                              <p className="my-1 fs-13">
                                GST :{" "}
                                <span className="fw-600 fs-14">
                                  {selectedCustomer.customerData.gstnumber}
                                </span>
                              </p>
                            )} */}
                                  {/* {selectedCustomer.customerData.state && (
                              <p className="my-1 fs-13">
                                State:{" "}
                                <span className="fw-600 fs-14">
                                  {selectedCustomer.customerData.state}
                                </span>
                              </p>
                            )} */}
                                </>
                              ) : (
                                <p className="my-2 text-muted">
                                  No supplier found
                                </p>
                              )}
                            </div>
                          </div>
                          {/* <CustomerInfo selectedCustomer={selectedCustomer?.customerData} onEditModel={openEditModal} paylater={selectedCustomer?.paylaterData}/> */}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* FOOTER */}
            <div className="modal-footer d-block">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
              >
                <XIcon/> Cancel
              </button>

              <button
                type="button"
                ref={addButtonRef}
                className="btn btn-primary text-right"
                onClick={() => {
                  if (!purchaseDate) {
                    dateRef.current?.focus();
                    return;
                  }

                  if (!purchaseNumber.trim()) {
                    numberRef.current?.focus();
                    return;
                  }

                  if (!selectedCustomer?._id) {
                    showAlert("warning", "Please select a supplier");
                    return;
                  }

                  handleAdd({
                    purchaseNo: purchaseNumber.trim(),
                    purchaseDate: new Date(purchaseDate).toISOString(),
                    supplierId: selectedCustomer._id, // ✅ add this
                  });
                }}
              >
                <CheckIcon/> Create
              </button>
            </div>
          </div>
        </div>
        <AddSupplier
          isActive={isActive}
          isOpen={isCustomerModalOpen}
          onClose={handleCustomerModalClose}
          onData={isCustomerData}
          onAdd={handleCustomerAdd}
          isBtnLoading={isBtnLoading}
          groupName={groupName}
          // onNewGroupsIn={handleCustomerGroupAdd}
          // newGroup={newGroupName}
          resetAll={true}
          error={error}
          setErrors={setErrors}
          numberSuggest={numberSuggest}
          addNewCustomNumber={addNewCustomNumber}
        />
        {isEditModalOpen && (
          <EditSupplier
            isActive={isActive}
            editData={isEditData}
            onClose={closeEditModal}
            onEdit={handleUpdte}
            isBtnLoading={isBtnLoading}
            groupName={groupName}
            // onNewGroupsIn={handleCustomerGroupAdd}
            resetAll={true}
            // newGroup={newGroupName}
            error={error}
            setErrors={setErrors}
          />
        )}
      </div>
    </>
  );
};

export default AddPurchase;
